import { GPUAPIObject } from './GPUAPIObject'
import { GPUBuffer, GPUElementArrayBuffer } from './GPUBuffer'
import { GPUTexture, GPUTexture2D } from './GPUTexture'
import * as GL from './webgl2constants'
import { gltype2dim, isInt } from './GPUAPITools'
import { mat3, vec2, vec3 } from './vec'
import { vec4 } from './GPUData/GPUData'
import { typeIsTexture } from './GPUTextureHelper'

class GPUUniform {
	program: GPUProgram
	size: GLint
	type: GLenum
	location: WebGLUniformLocation

	/** set and get gpu memory content */
	get gpumem() {return this.get()}
	set gpumem(data: Array<number>|Float32Array|number|vec2|vec3|vec4|mat3) {this.set(data)}

	// TODO : what about Float64 ? are they properly supported ?
	set(data: Float32Array|number|vec2|vec3|vec4|mat3|Array<number>, srcOffset?: number, srcLength?: number, transpose: boolean = false) {

		// tolerate data to be a number when 1D
		let d: Float32Array = data instanceof Float32Array ? data : typeof data == "number" ? new Float32Array([data]) : new Float32Array(data as Array<number>)

		this.program.use( () => {
			switch(this.type) {
				case GL.FLOAT:             this.program.gl.uniform1fv(this.location, d, srcOffset, srcLength);break
				case GL.FLOAT_VEC2:        this.program.gl.uniform2fv(this.location, d, srcOffset, srcLength);break
				case GL.FLOAT_VEC3:        this.program.gl.uniform3fv(this.location, d, srcOffset, srcLength);break
				case GL.FLOAT_VEC4:        this.program.gl.uniform4fv(this.location, d, srcOffset, srcLength);break

				case GL.INT:               this.program.gl.uniform1iv(this.location, d, srcOffset, srcLength);break // TODO test if read properly
				case GL.INT_VEC2:          this.program.gl.uniform2iv(this.location, d, srcOffset, srcLength);break // TODO test if read properly
				case GL.INT_VEC3:          this.program.gl.uniform3iv(this.location, d, srcOffset, srcLength);break // TODO test if read properly
				case GL.INT_VEC4:          this.program.gl.uniform4iv(this.location, d, srcOffset, srcLength);break // TODO test if read properly

				case GL.UNSIGNED_INT:      this.program.gl.uniform1uiv(this.location, d, srcOffset, srcLength);break // TODO test if read properly
				case GL.UNSIGNED_INT_VEC2: this.program.gl.uniform2uiv(this.location, d, srcOffset, srcLength);break // TODO test if read properly
				case GL.UNSIGNED_INT_VEC3: this.program.gl.uniform3uiv(this.location, d, srcOffset, srcLength);break // TODO test if read properly
				case GL.UNSIGNED_INT_VEC4: this.program.gl.uniform4uiv(this.location, d, srcOffset, srcLength);break // TODO test if read properly

				case GL.BOOL:              this.program.gl.uniform1iv(this.location, d, srcOffset, srcLength);break // TODO test if read properly
				case GL.BOOL_VEC2:         this.program.gl.uniform2iv(this.location, d, srcOffset, srcLength);break // TODO test if read properly
				case GL.BOOL_VEC3:         this.program.gl.uniform3iv(this.location, d, srcOffset, srcLength);break // TODO test if read properly
				case GL.BOOL_VEC4:         this.program.gl.uniform4iv(this.location, d, srcOffset, srcLength);break // TODO test if read properly

				case GL.FLOAT_MAT2:        this.program.gl.uniformMatrix2fv(this.location, transpose, d, srcOffset, srcLength);break
				case GL.FLOAT_MAT3:        this.program.gl.uniformMatrix3fv(this.location, transpose, d, srcOffset, srcLength);break
				case GL.FLOAT_MAT4:        this.program.gl.uniformMatrix4fv(this.location, transpose, d, srcOffset, srcLength);break

				case GL.FLOAT_MAT2x3:      this.program.gl.uniformMatrix2x3fv(this.location, transpose, d, srcOffset, srcLength); break
				case GL.FLOAT_MAT2x4:      this.program.gl.uniformMatrix2x4fv(this.location, transpose, d, srcOffset, srcLength); break
				case GL.FLOAT_MAT3x2:      this.program.gl.uniformMatrix3x2fv(this.location, transpose, d, srcOffset, srcLength); break
				case GL.FLOAT_MAT3x4:      this.program.gl.uniformMatrix3x4fv(this.location, transpose, d, srcOffset, srcLength); break
				case GL.FLOAT_MAT4x2:      this.program.gl.uniformMatrix4x2fv(this.location, transpose, d, srcOffset, srcLength); break
				case GL.FLOAT_MAT4x3:      this.program.gl.uniformMatrix4x3fv(this.location, transpose, d, srcOffset, srcLength); break

				case GL.SAMPLER_2D:
				case GL.SAMPLER_3D:
				case GL.SAMPLER_CUBE:
				case GL.SAMPLER_CUBE_SHADOW:
				case GL.SAMPLER_2D_SHADOW:
				case GL.SAMPLER_2D_ARRAY:
				case GL.SAMPLER_2D_ARRAY_SHADOW:
				case GL.INT_SAMPLER_2D:
				case GL.INT_SAMPLER_3D:
				case GL.INT_SAMPLER_CUBE:
				case GL.INT_SAMPLER_2D_ARRAY:
				case GL.UNSIGNED_INT_SAMPLER_2D:
				case GL.UNSIGNED_INT_SAMPLER_3D:
				case GL.UNSIGNED_INT_SAMPLER_CUBE:
				case GL.UNSIGNED_INT_SAMPLER_2D_ARRAY:
					this.program.gl.uniform1iv(this.location, d, srcOffset, srcLength) // TODO test if read properly
					break
			}
		})
	}
	get(): any {
		let out
		this.program.use((program) => {
			out = this.program.gl.getUniform(program,this.location)
		})
		return out
	}
	constructor(program: GPUProgram, size: number, type: GLenum, location: WebGLUniformLocation) {
		this.program = program
		this.size = size
		this.type = type
		this.location = location
	}
}

class VAO extends GPUAPIObject<WebGLVertexArrayObject> {
	deleteFunction() { this.gl.deleteVertexArray(this.obj) }
	createFunction() { return this.gl.createVertexArray() }
	bind() {
		let previousVAO = this.gl.getParameter(GL.VERTEX_ARRAY_BINDING)
		this.gl.bindVertexArray(this.obj)
		return () => this.gl.bindVertexArray(previousVAO)
	}
}

// same as VAO but for textures... it doesn't exist in webgl
export class TAO extends GPUAPIObject<Array<GPUTexture2D>> {
	// tao: Record<GLint,GPUTexture2D> = {}
	get tao() { return this.obj }
	set tao(tao) { this.obj = tao }
	set(texUnit: GLint, texture?: GPUTexture2D): this {
		this.tao = this.checkValidity()
		if (texture) this.tao[texUnit] = texture
		else delete this.tao[texUnit]
		return this
	}
	createFunction() { return [] }
	deleteFunction() {}
	bind() {
		this.tao = this.checkValidity()
		// let prev = this.tao.map( (t,k) => t.bind(k))
		let prev = this.tao.map( (t,k) => {
			// console.log(t)
			// console.log(t.bind)
			let out = t.bind(k)
			// console.log(out)
			return out
		})
		// console.log(this.tao)
		// console.log(prev)
		return () => prev.map(finish => finish())
	}
}

class GPUAttrib {
	program: GPUProgram
	size: GLint
	type: GLenum
	location: GLint

	/** set and get gpu memory content */
	// get gpumem() { return this.get() }
	set gpumem(data: GPUBuffer | null) { this.set(data) }

	// TODO add constant version as well (cf vertexAttrib[1234][f/i/ui][v])
	set(data: GPUBuffer | null, size?: GLint, type?: GLenum, normalized: GLboolean = false, stride: GLsizei = 0, offset: GLintptr = 0) {
		// TODO use VAO... this won't work with multiple programs
		this.program.vao.use( () => {
			console.log(data)
			if(data != null) {
				data.use(()=> {
					// !!! TODO FIXME this has to be tested ! GPUBuffer should provide us some info.
					// not all formats are allowed, only GL.BYTE GL.SHORT GL.UNSIGNED_BYTE GL.UNSIGNED_SHORT GL.FLOAT GL.HALF_FLOAT
					type = type || data.type
					size = size == undefined ? gltype2dim(this.type) : size // TODO FIXME
					if(isInt(type)) {
						this.program.gl.vertexAttribIPointer(this.location, size, type, stride, offset)
					} else {
						this.program.gl.vertexAttribPointer(this.location, size, type, normalized, stride, offset)
					}
					this.program.gl.enableVertexAttribArray(this.location)
				})
			} else {
				// this.program.gl.bindBuffer(???, null)
				this.program.gl.enableVertexAttribArray(this.location)
			}
		})
	}

	// TODO add constant version as well
	// get(): GPUBuffer {
	// 	let out
	// 	this.program.use((program) => {
	// 		out = this.gl.getUniform(program, this.location)
	// 	})
	// 	return out
	// }
	constructor(program: GPUProgram, size: number, type: GLenum, location: GLint) {
		this.program = program
		this.size = size
		this.type = type
		this.location = location
	}
}

class Elements {
	vao: VAO
	buf: GPUElementArrayBuffer | null = null
	constructor(vao: VAO) {
		this.vao = vao;
	}
	set gpumem(buf: GPUElementArrayBuffer | null) {
		this.buf = buf
		let useEnd: () => void = () => { }
		this.vao.use(() => {
			if (buf != null) useEnd = buf.bind()
			// else TODO unbind element array
		})
		useEnd()
		if(buf) {
			switch(buf.type) {
				case GL.UNSIGNED_BYTE:
				case GL.UNSIGNED_SHORT:
				case GL.UNSIGNED_INT:
					break
				default:
					throw "elements must be uint8 uint16 or uint32"
			}
		}
	}
}

/**
 * GPUProgram has a VAO attached to it. using the GPUProgram will also use that VAO
 * In the futur, I might consider separating these. but for my use case, this is OK
 */
// TODO use GPUAPIObject
export class GPUProgram {

	// TODO
	// this.gl.getFragDataLocation(this.program,"color_out_name")
	// this.gl.bindAttribLocation(this.program,index,"attribName")
	
	gl: WebGL2RenderingContext
	program: WebGLProgram | null
	readonly uniforms: Record<string, GPUUniform> = {} // TODO : gettable but not settable, so that no accident can happen
	readonly attribs: Record<string, GPUAttrib> = {}
	readonly elements: Elements
	vao: VAO
	tao: TAO

	setUniform(name: string, value: Array<number>|Float32Array|number|vec2|vec3|vec4|mat3|GPUTexture2D): this {
		if(value instanceof GPUTexture) {
			let u: GPUUniform = this.uniforms[name]
			if(!u) return this // fail
			if (typeIsTexture(u.type)) this.tao.set(u.gpumem as number, value)

		} else {
			if (this.uniforms[name]) this.uniforms[name].gpumem = value
		}
		return this
	}

	showUniforms() {
		for(let k in this.uniforms) {
			console.log(`${k} : [${this.uniforms[k].gpumem}]`)
		}
	}

	// set elements(e: GPUElementArrayBuffer|null) {
	// 	let useEnd: () => void = ()=>{}
	// 	this.vao.use(() => {
	// 		if(e!=null)
	// 		useEnd = e.useStart()
	// 	})
	// 	useEnd()
	// }

	/** the #version header is automatically added, precision is also added by default to highp */
	constructor(gl: WebGL2RenderingContext, vshaderSrc: string, fshaderSrc: string) {
		this.gl = gl
		this.program = this.compileProgram(vshaderSrc, fshaderSrc)
		this.vao = new VAO(this.gl)
		this.tao = new TAO(this.gl)
		this.elements = new Elements(this.vao)

		let updateUniformsList = () => {
			this.program = this.checkIfValid()
			let n = this.gl.getProgramParameter(this.program, this.gl.ACTIVE_UNIFORMS)
			let textureIndex = 0
			for (let i = 0; i < n; i++) {
				let info = this.gl.getActiveUniform(this.program, i)
				// console.log(info)
				if (info) {
					let loc = this.gl.getUniformLocation(this.program, info.name)
					if (loc) {
						this.uniforms[info.name] = new GPUUniform(this, info.size, info.type, loc)
						if (typeIsTexture(info.type)) {
							/** each used texture is assigned an increasing number, ! if there are too much of them, this can fail ! */
							this.uniforms[info.name].gpumem = textureIndex++
						}
					}
					// else throw "internal error : uniform without location" // this can happen when an uniforme is used in dead code

				} else {
					throw "internal error : missing uniform"
				}
			}
		}
		let updateAttribsList = () => {
			this.program = this.checkIfValid()
			let n = this.gl.getProgramParameter(this.program, this.gl.ACTIVE_ATTRIBUTES)
			for (let i = 0; i < n; i++) {
				let info = this.gl.getActiveAttrib(this.program,i)
				// console.log(info)
				if (info) {
					let loc = this.gl.getAttribLocation(this.program, info.name)
					if (loc!=-1) this.attribs[info.name] = new GPUAttrib(this, info.size, info.type, loc)
					else throw "internal error : attrib without location"
				} else {
					throw "internal error : missing attrib"
				}
			}
		}
		updateUniformsList()
		updateAttribsList()
	}

	use(exec: (program: WebGLProgram) => void = () => { }) {
		this.vao.use(() => {
			this.tao.use(() => {
				this.program = this.checkIfValid()
				let gbefore = this.gl.getParameter(GL.CURRENT_PROGRAM)
				this.gl.useProgram(this.program)
				exec(this.program)
				this.gl.useProgram(gbefore)
			})
		})
	}

	drawArrays(mode: GLenum, count: GLsizei, first: GLint = 0) {
		this.use(() => {
			this.gl.drawArrays(mode, first, count)
		})
	}

	drawElements(mode: GLenum, count: GLsizei, offset: GLintptr = 0) {
		// TODO make count optional and use this.elements.buf length
		if (this.elements.buf==null) {
			console.log("error : elements not set")
			this.drawArrays(mode, count, offset) // fallback
			return
		}

		let t = this.elements.buf.type
		// let t = GL.UNSIGNED_BYTE
		// let t = GL.UNSIGNED_SHORT
		// let t = GL.UNSIGNED_INT
		this.use(() => {
			this.gl.drawElements(mode, count, t, offset)
		})
	}

	drawArraysPoints         (count: GLsizei, first: GLint = 0)  { this.drawArrays(GL.POINTS,           count, first) }
	drawArraysLineStrip      (count: GLsizei, first: GLint = 0)  { this.drawArrays(GL.LINE_STRIP,       count, first) }
	drawArraysLineLoop       (count: GLsizei, first: GLint = 0)  { this.drawArrays(GL.LINE_LOOP,        count, first) }
	drawArraysLines          (count: GLsizei, first: GLint = 0)  { this.drawArrays(GL.LINES,            count, first) }
	drawArraysTriangleStrip  (count: GLsizei, first: GLint = 0)  { this.drawArrays(GL.TRIANGLE_STRIP,   count, first) }
	drawArraysTriangleFan    (count: GLsizei, first: GLint = 0)  { this.drawArrays(GL.TRIANGLE_FAN,     count, first) }
	drawArraysTriangles      (count: GLsizei, first: GLint = 0)  { this.drawArrays(GL.TRIANGLES,        count, first) }
	drawElementsPoints       (count: GLsizei, offset: GLint = 0) { this.drawElements(GL.POINTS,         count, offset) }
	drawElementsLineStrip    (count: GLsizei, offset: GLint = 0) { this.drawElements(GL.LINE_STRIP,     count, offset) }
	drawElementsLineLoop     (count: GLsizei, offset: GLint = 0) { this.drawElements(GL.LINE_LOOP,      count, offset) }
	drawElementsLines        (count: GLsizei, offset: GLint = 0) { this.drawElements(GL.LINES,          count, offset) }
	drawElementsTriangleStrip(count: GLsizei, offset: GLint = 0) { this.drawElements(GL.TRIANGLE_STRIP, count, offset) }
	drawElementsTriangleFan  (count: GLsizei, offset: GLint = 0) { this.drawElements(GL.TRIANGLE_FAN,   count, offset) }
	drawElementsTriangles    (count: GLsizei, offset: GLint = 0) { this.drawElements(GL.TRIANGLES,      count, offset) }

	delete() {
		this.checkIfValid()
		this.gl.deleteProgram(this.program)
		this.program = null
		this.vao.delete()
	}

	/** @internal */ private checkIfValid(msg: string = "deleted"): WebGLProgram {
		if (this.program == null) { throw "program " + msg }
		// return WebGLProgram and removes the null possibility
		return this.program
	}

	/** @internal */ private compileShader(source: string, type: GLenum): WebGLShader {
		let shader: WebGLShader | null = this.gl.createShader(type)
		if (shader == null) throw "shader creation failed"
		let toCompile =
`#version 300 es
${type == GL.FRAGMENT_SHADER ? '#define FRAGMENT_SHADER' : type == GL.VERTEX_SHADER ? '#define VERTEX_SHADER' : ''}
precision highp float;
precision highp int;
precision highp sampler2D;
precision highp isampler2D;
precision highp usampler2D;
${source}`
		this.gl.shaderSource(shader, toCompile)
		this.gl.compileShader(shader)
		let success = this.gl.getShaderParameter(shader, GL.COMPILE_STATUS)
		if (success) { return shader }
		let error: string = (this.gl.getShaderInfoLog(shader) || "").replace(/\0/g, '')
		this.gl.deleteShader(shader)
		let addLinesToSrc = (src: string) => src.split("\n").map((value, index) => (1 + index).pad(4) + ": " + value).join("\n")
		throw 'could not compile '
		+ (type == GL.VERTEX_SHADER ? 'vertex' : (type == GL.FRAGMENT_SHADER ? 'fragment' : '???'))
		+ ' shader\n'
		+ '\n' + error
		+ '\n' + addLinesToSrc(toCompile)
	}

	/** @internal */ private linkProgram(vshader: WebGLShader, fshader: WebGLShader) {
		let program = this.gl.createProgram()
		if (program == null) throw "program creation failed"
		this.gl.attachShader(program, vshader)
		this.gl.attachShader(program, fshader)
		this.gl.linkProgram(program)
		this.gl.detachShader(program, vshader)
		this.gl.detachShader(program, fshader)
		this.gl.validateProgram(program) // TODO : is this required ?
		let success = this.gl.getProgramParameter(program, GL.LINK_STATUS)
		if (success) { return program }
		let error = (this.gl.getProgramInfoLog(program) || "").replace(/\0/g, '')
		this.gl.deleteProgram(program)
		throw "could not link shader\n" + error
	}

	/** @internal */ private compileProgram(vshaderSrc: string, fshaderSrc: string): WebGLProgram {
		let vshader = this.compileShader(vshaderSrc, GL.VERTEX_SHADER)
		let fshader = this.compileShader(fshaderSrc, GL.FRAGMENT_SHADER)
		let program = this.linkProgram(vshader, fshader)
		this.gl.deleteShader(vshader)
		this.gl.deleteShader(fshader)
		return program
	}

}

// `
// uniform float time;
// uniform vec2 resolution;
// uniform float aspect;
// uniform float NaN;
// uniform int iNaN;
// uniform sampler2D cosinusTex;
// uniform sampler2D sigmoidTex;
// float cosinus(float theta) { return texture(cosinusTex,vec2(0.5,theta/2./pi)).x; }
// float sinus(float theta)   { return cosinus(theta-pi/2.); }
// // float cosinus(float theta) { return cos(theta); } // these are crappy on intel GPUs
// // float sinus(float theta)   { return sin(theta); } // these are crappy on intel GPUs
// float sigmoid(float x) { return texture(sigmoidTex,vec2(0.5,x+0.5)).x; }

// vec4 tex1d(sampler2D tex, int index) {
// ivec2 res = textureSize(tex,0);
// 	return texelFetch(tex,ivec2(
// 		index % (res.x),
// 		index / res.x
// 	),0);
// }
// `
// 			+ shaderLibHeader()
// 			+ source
// 			+ shaderLib()
// 		this.lg.gl.shaderSource(shader, to_compile)
// 		this.lg.gl.compileShader(shader)
// 		let success = this.lg.gl.getShaderParameter(shader, GL.COMPILE_STATUS)
// 		if (success) { return shader }
// 		let error = this.lg.gl.getShaderInfoLog(shader)
// 		this.lg.gl.deleteShader(shader)
// 		let addLinesToSrc = (src: string) => src.split("\n").map((value, index) => (1 + index) + ": " + value).join("\n")
// 		throw "ERROR : could not compile shader\n" + error + '\n' + addLinesToSrc(to_compile)
// 	}

// 	use(exec: () => void) {
// 		let gbefore = this.lg.gl.getParameter(GL.CURRENT_PROGRAM)
// 		let [x, y, w, h] = this.lg.gl.getParameter(GL.VIEWPORT)
// 		this.lg.gl.useProgram(this.program)
// 		this.setUniform_float("time", [getTime()])
// 		this.setUniform_vec2("resolution", [w, h])
// 		this.setUniform_float("aspect", [w / h])
// 		this.setUniform_float("NaN", [NaN])
// 		this.setUniform_int("iNaN", [NaN])
// 		// this.setUniform("cosinusTex", this.lg.cosinusTex as Tex2d)
// 		// this.setUniform("sigmoidTex", this.lg.sigmoidTex as Tex2d)
// 		exec()
// 		this.lg.gl.useProgram(gbefore)
// 	}

// }
